import { useRef, useState } from 'react';

import { CloseOutlineIc, SearchOutlineIc } from '@dsch/dd-icons';

import { useMakeModelKeywordSearch } from './MakeModelKeywordSearch.hook';

import { DisplayContainer } from '../../components/Support/DisplayContainer/DisplayContainer';
import { MakeModelKeywordSearchModal } from './subcomponents/MakeModelKeywordSearchModal';
import { InputButton } from '../../components/Toolkit/InputButton/InputButton';

import type { MakeModelKeywordSearchProps } from './MakeModelKeywordSearch.typed';

import { useClickOutside } from '../../hooks/useClickOutside';

import {
  CloseIcon,
  InputWrapper,
  ListContainer,
  SearchIcon,
  SInput,
} from './MakeModelKeywordSearch.styled';

/**
 * A search interface for filtering vehicles by make, model, and keywords.
 *
 * Renders different UI based on screen size.
 */
export const MakeModelKeywordSearch = (props: MakeModelKeywordSearchProps) => {
  const {
    modalDisplayConfig,
    dropdownDisplayConfig,
    minPrice,
    maxPrice,
    minYear,
    maxYear,
  } = props;

  const [inputValue, setInputValue] = useState('');
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  const closeDropdown = () => {
    setDropdownIsOpen(false);
    setInputValue('');
  };

  useClickOutside({
    active: dropdownIsOpen,
    ref: dropdownContainerRef,
    onClickOutside: closeDropdown,
  });

  const { keyword } = useMakeModelKeywordSearch({
    minPrice,
    maxPrice,
    minYear,
    maxYear,
  });

  return (
    <>
      <DisplayContainer config={modalDisplayConfig}>
        <InputButton
          onClick={openModal}
          placeholder="All makes/models"
          isActive={Boolean(keyword)}
          value={keyword}
          aria-label="Open make & model modal"
        />
        <MakeModelKeywordSearchModal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
        />
      </DisplayContainer>
      <DisplayContainer config={dropdownDisplayConfig}>
        <div data-testid="make-model-keyword-search-browser">
          {!dropdownIsOpen && (
            <InputButton
              onClick={() => setDropdownIsOpen(true)}
              placeholder="All makes/models"
              isActive={false}
              value={keyword}
              aria-label="Open make & model dropdown"
              heightVariant="LARGE"
            />
          )}
          {dropdownIsOpen && (
            <div ref={dropdownContainerRef}>
              <InputWrapper>
                <SearchIcon aria-label="Search">
                  <SearchOutlineIc />
                </SearchIcon>
                <SInput
                  id="homepage-make-model-search-box-input"
                  name="homepage-make-model-search-box-input"
                  type="search"
                  autoComplete="off"
                  placeholder={'All makes/models'}
                  onChange={(e) => setInputValue(e.target.value)}
                  value={inputValue}
                  autoFocus={true}
                />
                {inputValue && (
                  <CloseIcon
                    type="button"
                    onClick={closeDropdown}
                    aria-label="Clear search"
                  >
                    <CloseOutlineIc width={12} height={12} />
                  </CloseIcon>
                )}
              </InputWrapper>
              <ListContainer
                data-testid="make-model-keyword-search-list"
                expanded
                maxHeight="337px"
              >
                TODO: Add list of makes/models here
              </ListContainer>
            </div>
          )}
        </div>
      </DisplayContainer>
    </>
  );
};
